import { SpreadDisplay } from '@src/components/TradeDirection/styles';
import {
  ButtonVariants,
  DateTimeDurationPicker,
  DateTimeDurationPickerValueType,
  Dialog,
  Divider,
  FormControlSizes,
  FormGroup,
  Grid,
  IconName,
  NumberInput,
  SearchSelect,
  VStack,
  getDisplayNameOrNameLabel,
  isDuration,
  isEmptyDuration,
  useSecurity,
  type Duration,
  type MarketAccount,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useCustomRateApi } from './customRateApi';
import {
  hasCustomRateErrors,
  resolveCustomRateValidUntil,
  validateCustomRateForm,
  type CustomRateFieldErrors,
  type CustomRateSideInput,
  type CustomRateValidUntilInput,
} from './customRateValidation';
import { getCustomRateSpreadDisplay } from './getCustomRateSpreadDisplay';

const NO_VALID_UNTIL_WARNING = 'No valid-until time: rate stays active until updated or cleared.';
const DEFAULT_VALID_UNTIL_DURATION: Duration = { days: 0, hours: 1, minutes: 0, seconds: 0 };
const VALID_UNTIL_DURATION_SHORTCUTS = {
  '30m': '30m',
  '1h': '1h',
  '4h': '4h',
  '8h': '8h',
  '12h': '12h',
  '1d': '1d',
} as const;

interface CustomRateFormInput {
  marketAccount?: MarketAccount;
  bid: CustomRateSideInput;
  offer: CustomRateSideInput;
  validUntil: CustomRateValidUntilInput;
}

interface PublishDialogProps extends UseDisclosureReturn {
  internalMarketAccounts: MarketAccount[];
  initialMarketAccount?: MarketAccount;
  symbol: string;
}

interface PublishCustomRateDialogProps {
  dialog?: PublishDialogProps;
}

export function PublishCustomRateDialog({ dialog }: PublishCustomRateDialogProps) {
  return dialog == null ? null : <PublishCustomRateDialogContent {...dialog} />;
}

function PublishCustomRateDialogContent({
  initialMarketAccount,
  internalMarketAccounts,
  symbol,
  ...disclosureProps
}: PublishDialogProps) {
  const security = useSecurity(symbol);
  const { close } = disclosureProps;
  const { publishCustomRate } = useCustomRateApi({
    displaySymbol: security?.DisplaySymbol ?? symbol,
    symbol,
  });
  const [formInput, setFormInput] = useState<CustomRateFormInput>(() => ({
    marketAccount: initialMarketAccount,
    bid: { price: '', size: '' },
    offer: { price: '', size: '' },
    validUntil: DEFAULT_VALID_UNTIL_DURATION,
  }));
  const [submitErrors, setSubmitErrors] = useState<CustomRateFieldErrors>({});
  const { marketAccount, bid, offer, validUntil } = formInput;
  const spreadDisplay = useMemo(
    () => getCustomRateSpreadDisplay({ bidPrice: bid.price, offerPrice: offer.price, security }),
    [bid.price, offer.price, security]
  );

  const clearSubmitError = useCallback((field: keyof CustomRateFieldErrors) => {
    setSubmitErrors(currentErrors => {
      if (currentErrors[field] == null) {
        return currentErrors;
      }
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const updateFormInput = useCallback(
    <Field extends keyof CustomRateFormInput>(
      field: Field,
      value: CustomRateFormInput[Field],
      errorField?: keyof CustomRateFieldErrors
    ) => {
      setFormInput(currentFormInput => ({ ...currentFormInput, [field]: value }));
      if (errorField != null) {
        clearSubmitError(errorField);
      }
    },
    [clearSubmitError]
  );

  const updateBid = useCallback(
    (bidUpdate: Partial<CustomRateSideInput>, errorField: keyof CustomRateFieldErrors) => {
      setFormInput(currentFormInput => ({ ...currentFormInput, bid: { ...currentFormInput.bid, ...bidUpdate } }));
      clearSubmitError(errorField);
    },
    [clearSubmitError]
  );

  const updateOffer = useCallback(
    (offerUpdate: Partial<CustomRateSideInput>, errorField: keyof CustomRateFieldErrors) => {
      setFormInput(currentFormInput => ({ ...currentFormInput, offer: { ...currentFormInput.offer, ...offerUpdate } }));
      clearSubmitError(errorField);
    },
    [clearSubmitError]
  );

  const updateValidUntil = useCallback(
    (newValidUntil: CustomRateValidUntilInput) => {
      const resolvedValidUntil = isDuration(newValidUntil) && isEmptyDuration(newValidUntil) ? null : newValidUntil;
      updateFormInput('validUntil', resolvedValidUntil, 'validUntil');
    },
    [updateFormInput]
  );

  const handlePublish = useCallback(() => {
    const publishTime = new Date();
    const errors = validateCustomRateForm({ marketAccount, bid, offer, validUntil, relativeTo: publishTime });
    setSubmitErrors(errors);
    if (hasCustomRateErrors(errors) || marketAccount == null) {
      return;
    }

    if (
      publishCustomRate({ marketAccount, bid, offer, validUntil: resolveCustomRateValidUntil(validUntil, publishTime) })
    ) {
      close();
    }
  }, [bid, close, marketAccount, offer, publishCustomRate, validUntil]);

  return (
    <Dialog
      {...disclosureProps}
      dataTestId="publish-custom-rate-dialog"
      title={`Publish Custom Rate: ${symbol}`}
      headerIcon={IconName.Pencil}
      showClose
      width={480}
      cancelLabel="Cancel"
      confirmLabel="Publish Rate"
      variant={ButtonVariants.Primary}
      onConfirm={handlePublish}
      closeOnConfirm={false}
      showFooterBorder={false}
    >
      <VStack gap="spacing24" alignItems="stretch">
        <FormGroup
          label="Account"
          tooltip="Select an active trading account on the internal market."
          error={submitErrors.marketAccount}
          mb={0}
        >
          <SearchSelect
            data-testid="publish-custom-rate-account"
            selection={marketAccount}
            options={internalMarketAccounts}
            onChange={newMarketAccount => updateFormInput('marketAccount', newMarketAccount, 'marketAccount')}
            getLabel={getDisplayNameOrNameLabel}
            placeholder="Select internal account"
            invalid={submitErrors.marketAccount != null}
          />
        </FormGroup>

        <Grid columns="minmax(0, 1fr) max-content minmax(0, 1fr)" columnGap="spacing12" rowGap="spacing24">
          <FormGroup size={FormControlSizes.Small} label="Bid Price" error={submitErrors.bidPrice} mb={0}>
            <NumberInput
              data-testid="publish-custom-rate-bid-price"
              value={bid.price}
              onChange={price => updateBid({ price }, 'bidPrice')}
              invalid={submitErrors.bidPrice != null}
              placeholder="0.00"
              suffix={security?.QuoteCurrency}
            />
          </FormGroup>
          <VStack alignSelf="end" position="relative" h="spacing24" minWidth="4rem">
            <SpreadDisplay unit={spreadDisplay?.unit}>
              <p data-testid="publish-custom-rate-spread">{spreadDisplay?.value ?? '--'}</p>
            </SpreadDisplay>
          </VStack>
          <FormGroup size={FormControlSizes.Small} label="Offer Price" error={submitErrors.offerPrice} mb={0}>
            <NumberInput
              data-testid="publish-custom-rate-offer-price"
              value={offer.price}
              onChange={price => updateOffer({ price }, 'offerPrice')}
              invalid={submitErrors.offerPrice != null}
              placeholder="0.00"
              suffix={security?.QuoteCurrency}
            />
          </FormGroup>
          <FormGroup size={FormControlSizes.Small} label="Bid Size" error={submitErrors.bidSize} mb={0}>
            <NumberInput
              data-testid="publish-custom-rate-bid-size"
              value={bid.size}
              onChange={size => updateBid({ size }, 'bidSize')}
              invalid={submitErrors.bidSize != null}
              placeholder="0"
              suffix={security?.BaseCurrency}
            />
          </FormGroup>
          <FormGroup
            size={FormControlSizes.Small}
            label="Offer Size"
            error={submitErrors.offerSize}
            mb={0}
            // Prevent grid auto-placement from moving offer size into the spread column.
            gridColumn={3}
            gridRow={2}
          >
            <NumberInput
              data-testid="publish-custom-rate-offer-size"
              value={offer.size}
              onChange={size => updateOffer({ size }, 'offerSize')}
              invalid={submitErrors.offerSize != null}
              placeholder="0"
              suffix={security?.BaseCurrency}
            />
          </FormGroup>
        </Grid>

        <Divider orientation="horizontal" />

        <FormGroup
          size={FormControlSizes.Small}
          label="Valid Until"
          tooltip="Choose an expiry time or duration. Durations are converted to a fixed expiry when published."
          error={submitErrors.validUntil}
          warning={validUntil == null ? NO_VALID_UNTIL_WARNING : undefined}
        >
          <DateTimeDurationPicker
            dataTestId="publish-custom-rate-valid-until"
            value={validUntil}
            onChange={newValidUntil => updateValidUntil(newValidUntil.value)}
            initialEntryMode={DateTimeDurationPickerValueType.Duration}
            shortcuts={VALID_UNTIL_DURATION_SHORTCUTS}
            calcDurationRelativeTo={new Date()}
            size={FormControlSizes.Small}
            portalize
            invalid={submitErrors.validUntil != null}
          />
        </FormGroup>
      </VStack>
    </Dialog>
  );
}
